import Icon from '../ui/Icon';
import StatusBadge from '../ui/StatusBadge';
import BrandLogo from '../ui/BrandLogo';
import { ErrorState } from '../ui/States';

const pct = (fraction) => `${Math.round(Math.max(0, Math.min(1, fraction)) * 100)}%`;

/** Track across all milestones with the user's position and a marker per level. */
function MilestoneTrack({ rewards }) {
  const { approved, levels, maxScansRequired: max, next, allTiersCompleted } = rewards;
  return (
    <div className="px-5 pt-5 pb-8">
      <div className="flex items-baseline justify-between gap-3 mb-3 text-xs text-on-surface-variant">
        <span>
          <strong className="font-data-mono text-on-surface">{approved}</strong> approved scans
        </span>
        <span>
          {next && (
            <>
              <strong className="font-data-mono text-on-surface">{next.scansRemaining}</strong> more to unlock Level {next.level}
            </>
          )}
          {!next && allTiersCompleted && 'All milestones reached'}
        </span>
      </div>
      <div
        className="relative h-2.5 rounded-full bg-surface-container"
        role="progressbar"
        aria-label="Progress towards the final reward level"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={Math.min(approved, max)}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-secondary to-status-ok-dot"
          style={{ width: pct(approved / max) }}
        />
        {levels.map((level) => (
          <div
            key={level.level}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: pct(level.scansRequired / max) }}
          >
            <span
              className={`w-4 h-4 rounded-full border-2 ${
                level.reached ? 'bg-secondary border-surface-container-lowest' : 'bg-surface-container-lowest border-outline-variant'
              }`}
              aria-hidden="true"
            />
            {/* The final marker sits at the track's end, so its label hangs left instead of overflowing */}
            <span
              className={`absolute top-5 whitespace-nowrap font-data-mono text-[10px] text-on-surface-variant ${
                level.scansRequired === max ? 'right-0' : ''
              }`}
            >
              L{level.level} · {level.scansRequired}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RewardDetail({ label, children, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-on-surface-variant">{label}</dt>
      <dd className={`text-on-surface text-right min-w-0 break-all ${mono ? 'font-data-mono font-semibold' : ''}`}>{children}</dd>
    </div>
  );
}

function LevelCard({ level }) {
  return (
    <li
      className={`flex flex-col gap-3 rounded-xl border p-4 ${
        level.state.tone === 'ok' ? 'border-status-ok-border bg-status-ok-bg/40' : 'border-outline-variant/30 bg-surface-container-lowest'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <BrandLogo url={level.logoUrl} brand={level.brandName} size="lg" />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant">Level {level.level}</p>
            <p className="text-sm font-bold text-on-surface truncate">{level.brandName}</p>
          </div>
        </div>
        <StatusBadge tone={level.state.tone}>{level.state.label}</StatusBadge>
      </div>

      <p className="text-[13px] text-on-surface min-h-9">{level.offerText}</p>

      {level.reward && (
        <dl className="flex flex-col gap-1 text-[11px]">
          {level.reward.couponCode && (
            <RewardDetail label="Code" mono>
              {level.reward.couponCode}
            </RewardDetail>
          )}
          <RewardDetail label="Unlocked">{level.reward.unlockedAt}</RewardDetail>
          <RewardDetail label="Expires">{level.reward.expiresAt}</RewardDetail>
        </dl>
      )}

      <div className="mt-auto">
        <div className="flex items-center justify-between text-[11px] text-on-surface-variant mb-1.5">
          <span className="font-data-mono">{level.scansRequired} scans</span>
          <span className={level.reached ? 'text-secondary font-semibold' : ''}>
            {level.reached ? 'Milestone reached' : `${level.scansToGo} to go`}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-surface-container overflow-hidden" aria-hidden="true">
          <div className={`h-full rounded-full ${level.reached ? 'bg-secondary' : 'bg-primary-fixed-dim'}`} style={{ width: pct(level.fill) }} />
        </div>
      </div>
    </li>
  );
}

function RewardsSkeleton() {
  return (
    <div className="p-5 flex flex-col gap-5 animate-pulse" role="status" aria-label="Loading reward levels">
      <div className="h-2.5 rounded-full bg-surface-container" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="h-40 rounded-xl bg-surface-container-low" />
        ))}
      </div>
    </div>
  );
}

/** Rewards section of the user profile. It loads and fails on its own, without affecting the rest of the page. */
export default function RewardLevels({ rewards, isLoading, error, onRetry }) {
  const forbidden = error?.status === 403;
  return (
    <section
      aria-labelledby="rewards-heading"
      className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-surface-container">
        <h2 id="rewards-heading" className="text-lg font-bold text-primary">
          Reward levels
        </h2>
      </div>

      {isLoading && <RewardsSkeleton />}

      {!isLoading && error && (
        <ErrorState
          icon={forbidden ? 'lock' : 'cloud_off'}
          title={forbidden ? "You don't have permission to view these rewards" : "Couldn't load the reward levels"}
          description={forbidden ? undefined : error.message}
          retryAfter={error.status === 429 ? 5 : 0}
          onRetry={forbidden ? undefined : onRetry}
        />
      )}

      {!isLoading && !error && rewards && (
        <>
          {!rewards.hasActivePackage && (
            <p className="mx-5 mt-5 flex items-center gap-2 rounded-lg border border-status-pending-border bg-status-pending-bg px-3 py-2 text-xs text-status-pending-text">
              <Icon name="info" size={16} />
              No coupon package is active yet.
            </p>
          )}
          {rewards.levels.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-on-surface-variant">No reward levels are configured.</p>
          ) : (
            <>
              <MilestoneTrack rewards={rewards} />
              <ol className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 px-5 pb-5">
                {rewards.levels.map((level) => (
                  <LevelCard key={level.level} level={level} />
                ))}
              </ol>
            </>
          )}
        </>
      )}
    </section>
  );
}
